import { useState, useEffect } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { logOut } from '../lib/firebase'
import { cn } from '../lib/utils'
import AppFooter from './AppFooter'
import GlobalSearch from './GlobalSearch'
import { AuroraBackground } from './ui'
import {
  Home,
  MessageSquare,
  Search,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  Layers,
  Sun,
  Moon,
  ChevronDown,
  Command,
  Sparkles,
  Plus,
} from 'lucide-react'

const navItems = [
  { path: '/', icon: Home, label: 'Home', end: true },
  { path: '/kb', icon: Layers, label: 'Workspace' },
  { path: '/chat', icon: MessageSquare, label: 'Chat' },
  { path: '/search', icon: Search, label: 'Search' },
]

// Get 2-letter initials from user name or email
function getInitials(user) {
  if (user?.displayName) {
    const names = user.displayName.trim().split(' ')
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase()
    } else if (names.length === 1 && names[0].length > 0) {
      return names[0][0].toUpperCase()
    }
  }
  if (user?.email) {
    return user.email[0].toUpperCase()
  }
  return 'U'
}

export default function Layout() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [avatarError, setAvatarError] = useState(false)
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark') ||
        localStorage.getItem('theme') === 'dark' ||
        (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches)
    }
    return false
  })

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }, [darkMode])

  // Global keyboard shortcut for search (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    // Also listen for custom event to open search programmatically
    const handleOpenSearch = () => {
      setSearchOpen(true)
    }
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('openGlobalSearch', handleOpenSearch)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('openGlobalSearch', handleOpenSearch)
    }
  }, [])

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  const handleLogout = async () => {
    await logOut()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-background flex relative">
      {/* Aurora Background */}
      <AuroraBackground />

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:sticky inset-y-0 left-0 z-50 flex flex-col",
          "lg:top-4 lg:left-4 lg:bottom-4 lg:rounded-2xl",
          "sidebar-glass border-r lg:border border-border/50",
          "transition-all duration-300 ease-smooth",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
          sidebarCollapsed ? "w-[72px]" : "w-72",
          "lg:h-[calc(100vh-2rem)]"
        )}
      >
        {/* Logo */}
        <div className={cn(
          "h-16 flex items-center border-b border-border/50 px-4",
          sidebarCollapsed ? "justify-center" : "justify-between"
        )}>
          <a
            href="https://sigmanote.app"
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "flex items-center gap-3 hover:opacity-80 transition-opacity",
              sidebarCollapsed && "justify-center"
            )}
          >
            <div className="relative">
              <img
                src="/assets/logo.png"
                alt="SigmaNote"
                className="w-9 h-9 rounded-xl shadow-md"
              />
              <div className="absolute inset-0 rounded-xl ring-2 ring-primary/20" />
            </div>
            {!sidebarCollapsed && (
              <div className="flex flex-col">
                <span className="font-bold text-lg tracking-tight">SigmaNote</span>
                <span className="text-[10px] text-muted-foreground -mt-0.5">Knowledge Hub</span>
              </div>
            )}
          </a>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-xl hover:bg-accent transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1.5 overflow-y-auto">
          {/* Quick action button */}
          {!sidebarCollapsed && (
            <button
              onClick={() => navigate('/kb?import=true')}
              className="w-full flex items-center gap-3 px-4 py-3 mb-4 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-lg shadow-primary/25 group"
            >
              <Plus className="w-5 h-5" />
              <span className="font-medium">New</span>
              <Sparkles className="w-4 h-4 ml-auto opacity-70 group-hover:opacity-100 transition-opacity" />
            </button>
          )}

          {sidebarCollapsed && (
            <button
              onClick={() => navigate('/kb?import=true')}
              className="w-full flex items-center justify-center p-3 mb-4 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-lg shadow-primary/25"
              title="New"
            >
              <Plus className="w-5 h-5" />
            </button>
          )}

          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                  sidebarCollapsed && "justify-center px-3",
                  isActive
                    ? "bg-primary/10 text-primary font-medium shadow-sm"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                )
              }
            >
              <item.icon className={cn("w-5 h-5 flex-shrink-0")} />
              {!sidebarCollapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Bottom section */}
        <div className="mt-auto border-t border-border/50">
          {/* Settings link */}
          <div className="p-3">
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                  sidebarCollapsed && "justify-center px-3",
                  isActive
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                )
              }
            >
              <Settings className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && <span>Settings</span>}
            </NavLink>
          </div>

          {/* Collapse toggle */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={cn(
              "hidden lg:flex items-center gap-2 w-full px-4 py-3 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors border-t border-border/50",
              sidebarCollapsed ? "justify-center" : "justify-start"
            )}
          >
            <ChevronLeft className={cn(
              "w-4 h-4 transition-transform duration-300",
              sidebarCollapsed && "rotate-180"
            )} />
            {!sidebarCollapsed && <span className="text-sm">Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className={cn(
        "flex-1 flex flex-col min-h-screen transition-all duration-300",
        sidebarCollapsed ? "lg:pl-4" : "lg:pl-4"
      )}>
        {/* Top bar */}
        <header className={cn(
          "h-16 flex items-center justify-between px-4 lg:px-6",
          "sticky top-0 z-30",
          "lg:mt-4 lg:mx-4 lg:rounded-2xl",
          "glass-card"
        )}>
          {/* Left: Mobile menu + Search trigger */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2.5 rounded-xl hover:bg-accent transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Logo on mobile */}
            <a
              href="https://sigmanote.app"
              target="_blank"
              rel="noopener noreferrer"
              className="lg:hidden flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <img
                src="/assets/logo.png"
                alt="SigmaNote"
                className="w-8 h-8 rounded-xl shadow-sm"
              />
              <span className="font-bold">SigmaNote</span>
            </a>

            {/* Search trigger on desktop */}
            <button
              onClick={() => setSearchOpen(true)}
              className="hidden lg:flex items-center gap-3 px-4 py-2.5 rounded-xl bg-muted/50 hover:bg-muted border border-border/50 text-muted-foreground hover:text-foreground transition-all min-w-[280px] group"
            >
              <Search className="w-4 h-4" />
              <span className="text-sm flex-1 text-left">Search knowledge base...</span>
              <kbd className="flex items-center gap-1 px-2 py-1 rounded-lg bg-background/80 border border-border text-xs group-hover:bg-background transition-colors">
                <Command className="w-3 h-3" />K
              </kbd>
            </button>
          </div>

          {/* Right: Actions + User menu */}
          <div className="flex items-center gap-2">
            {/* Mobile search button */}
            <button
              onClick={() => setSearchOpen(true)}
              className="lg:hidden p-2.5 rounded-xl hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Dark/Light Mode Toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-2.5 rounded-xl hover:bg-accent text-muted-foreground hover:text-foreground transition-all group"
              title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              <div className="relative w-5 h-5">
                <Sun className={cn(
                  "w-5 h-5 absolute inset-0 transition-all duration-300",
                  darkMode ? "rotate-0 scale-100 opacity-100" : "rotate-90 scale-0 opacity-0"
                )} />
                <Moon className={cn(
                  "w-5 h-5 absolute inset-0 transition-all duration-300",
                  darkMode ? "rotate-90 scale-0 opacity-0" : "rotate-0 scale-100 opacity-100"
                )} />
              </div>
            </button>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-accent transition-colors"
              >
                {user?.photoURL && user.photoURL.trim() !== '' && !avatarError ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || 'User avatar'}
                    className="w-9 h-9 rounded-xl object-cover ring-2 ring-border"
                    onError={() => setAvatarError(true)}
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary/80 to-purple-500/80 flex items-center justify-center ring-2 ring-border">
                    <span className="text-sm font-bold text-white">
                      {getInitials(user)}
                    </span>
                  </div>
                )}
                <ChevronDown className={cn(
                  "w-4 h-4 text-muted-foreground transition-transform duration-200 hidden sm:block",
                  userMenuOpen && "rotate-180"
                )} />
              </button>

              {/* Dropdown */}
              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-64 rounded-2xl border border-border/50 glass-card shadow-xl z-50 overflow-hidden animate-scale-in">
                    {/* User Info */}
                    <div className="p-4 border-b border-border/50 bg-muted/30">
                      <div className="flex items-center gap-3">
                        {user?.photoURL && user.photoURL.trim() !== '' && !avatarError ? (
                          <img
                            src={user.photoURL}
                            alt={user.displayName || 'User avatar'}
                            className="w-10 h-10 rounded-xl object-cover ring-2 ring-border"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/80 to-purple-500/80 flex items-center justify-center">
                            <span className="text-sm font-bold text-white">
                              {getInitials(user)}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{user?.displayName || 'User'}</p>
                          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="p-2">
                      <NavLink
                        to="/settings"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm hover:bg-accent transition-colors"
                      >
                        <Settings className="w-4 h-4 text-muted-foreground" />
                        <span>Settings</span>
                      </NavLink>
                    </div>

                    {/* Logout */}
                    <div className="p-2 border-t border-border/50">
                      <button
                        onClick={() => {
                          setUserMenuOpen(false)
                          handleLogout()
                        }}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm w-full text-destructive hover:bg-destructive/10 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sign out</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-hidden lg:px-4 lg:pt-4 flex flex-col">
          <Outlet />
        </div>

        {/* Footer */}
        <div className="lg:px-4">
          <AppFooter />
        </div>
      </main>

      {/* Global Search Modal */}
      <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  )
}
